/**
 * iter554 — Per-page JSON-LD structured data
 *
 * Тъй като React + Helmet рендерира meta tag-овете САМО client-side, някои
 * crawler-и (особено Facebook, Twitter, LinkedIn) ги пропускат. Затова:
 *
 *   1. Глобалните schema-та (Organization, WebSite, MedicalBusiness,
 *      SoftwareApplication, Person, Service, MedicalWebPage) са
 *      статично embed-нати в /public/index.html — видими БЕЗ JavaScript.
 *
 *   2. Този компонент добавя per-page schemas (FAQPage, BreadcrumbList,
 *      Article, MedicalCondition) през Helmet. Те остават client-side,
 *      но за Google и AI crawler-и (които правят JS rendering) добавят
 *      контекст за rich snippets.
 *
 * Usage examples:
 *   <PageStructuredData type="FAQPage" data={{ faqs: [{q, a}, ...] }} />
 *   <PageStructuredData type="Breadcrumb" data={{ items: [{name, url}, ...] }} />
 *   <PageStructuredData type="Article" data={{ title, description, image, datePublished }} />
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://nutramilo.com";

function buildSchema(type, data) {
  switch (type) {
    case "FAQPage":
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (data?.faqs || []).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      };

    case "Breadcrumb":
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: (data?.items || []).map((it, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          name: it.name,
          item: it.url?.startsWith("http") ? it.url : `${SITE}${it.url || ""}`,
        })),
      };

    case "Article":
      return {
        "@context": "https://schema.org",
        "@type": data?.medical ? "MedicalScholarlyArticle" : "Article",
        headline: data?.title,
        description: data?.description,
        image: data?.image || `${SITE}/app-icon.png`,
        author: { "@id": `${SITE}/#founder` },
        publisher: { "@id": `${SITE}/#organization` },
        datePublished: data?.datePublished,
        dateModified: data?.dateModified || data?.datePublished,
        mainEntityOfPage: data?.url || SITE,
        inLanguage: data?.lang || "bg-BG",
      };

    case "HowTo":
      return {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: data?.title,
        description: data?.description,
        step: (data?.steps || []).map((s, idx) => ({
          "@type": "HowToStep",
          position: idx + 1,
          name: s.name,
          text: s.text,
        })),
      };

    case "Course":
      return {
        "@context": "https://schema.org",
        "@type": "Course",
        name: data?.title,
        description: data?.description,
        provider: { "@id": `${SITE}/#organization` },
        inLanguage: "bg-BG",
      };

    case "Review":
      return {
        "@context": "https://schema.org",
        "@type": "Review",
        itemReviewed: { "@id": `${SITE}/#software` },
        author: { "@type": "Person", name: data?.author },
        reviewRating: {
          "@type": "Rating",
          ratingValue: data?.rating,
          bestRating: "5",
          worstRating: "1",
        },
        reviewBody: data?.body,
        datePublished: data?.datePublished,
      };

    default:
      return null;
  }
}

export default function PageStructuredData({ type, data }) {
  const schema = buildSchema(type, data);
  if (!schema) return null;
  return (
    <Helmet>
      <script type="application/ld+json" data-testid={`structured-data-${type.toLowerCase()}`}>
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
